import { useState, useMemo, useCallback } from 'react';

// Generic search hook for any data type
export const useGenericSearch = (data, searchKeys = ['text']) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});

  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.filter(item => {
      // Search term filtering
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const keys = Array.isArray(searchKeys) ? searchKeys : ['text'];
        const matchesSearch = keys.some(key => {
          const value = item[key];
          return value && value.toString().toLowerCase().includes(searchLower);
        });
        if (!matchesSearch) return false;
      }

      // Filter filtering
      return Object.entries(filters).every(([filterKey, filterValue]) => {
        // If no filter value, always pass
        if (filterValue === null || filterValue === undefined || filterValue === '') return true;
        
        const itemValue = item[filterKey];
        // If filter has a value but item doesn't have the field, fail the filter
        if (itemValue === undefined || itemValue === null) return false;

        // Handle different data types
        if (typeof filterValue === 'boolean') {
          return itemValue === filterValue;
        }
        
        if (typeof filterValue === 'number') {
          return parseInt(itemValue) === filterValue;
        }
        
        // String comparison (case-insensitive)
        return itemValue.toString().toLowerCase() === filterValue.toString().toLowerCase();
      });
    });
  }, [data, searchTerm, filters, searchKeys]);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  return {
    searchTerm,
    filteredData,
    filters,
    setSearchTerm,
    updateFilter,
    clearFilters
  };
};

// Courses search hook
export const useCoursesSearch = (courses) => {
  return useGenericSearch(courses, ['title', 'description']);
};

// Students search hook
export const useStudentsSearch = (students) => {
  return useGenericSearch(students, ['name', 'email']);
};

// Questions search hook
export const useQuestionsSearch = (questions) => {
  return useGenericSearch(questions, ['text', 'student_name', 'student_email', 'course_title', 'lesson_title']);
};
